import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useStyles as useZoneStyles } from './ImageZonesList.styles';
import { useStyles } from '../../../styles';
import FilterIcon from '@mui/icons-material/Filter';
import {
  getExamSheetImageZones,
  deleteExamTaskImageZones,
  deleteExamScoringSchemaImageZones,
} from '../../../../services/images/images.service';
import { AxiosResponse } from 'axios';
import {
  ExamTaskImageZone,
  ExamSheetImageZoneTableItem,
  ExamScoringSchemaImageZone,
} from '../../../../models/imageZone.types';
import { SelectableTableColumn } from '../../../../components/SelectableTable/SelectableTable.types';
import { SelectableTable } from '../../../../components/SelectableTable/SelectableTable.component';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@mui/icons-material/Add';
import TooltipOne from '../../../../components/TooltipOne/TooltipOne.component';
import { Prompt } from './../../../../components/Prompt/Prompt.component';
import { useLayoutContext } from '../../../../Context/Layout.context';
import ImageZonesEditor from './../../ImageZonesEditor/ImageZonesEditor.component';
import InfoShield from '../../../../components/InfoShield.component';
import { useTranslation } from 'react-i18next';

const columns: SelectableTableColumn[] = [
  {
    id: 'examTaskName',
    type: 'text',
    title: 'Zadanie',
    filterType: null,
  },
  {
    id: 'criterionName',
    type: 'text',
    title: 'Kryterium',
    filterType: null,
  },
  {
    id: 'imageZone',
    type: 'text',
    title: 'Strefa obrazu',
    filterType: null,
    centered: true,
  },
  {
    id: 'actions',
    type: 'text',
    title: 'Akcje',
    filterType: null,
    skipEnterMethod: true,
  },
];

interface ImageZonesListProps {
  sheetId: string;
  sheetSymbol: string;
}

const ImageZonesList: React.FC<ImageZonesListProps> = (props) => {
  const classes = useStyles();
  const zoneClasses = useZoneStyles();
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();

  const [examSheetImageZones, setExamSheetImageZones] = useState<ExamTaskImageZone[] | undefined>(undefined);
  const [examTaskGroupSolutions, setExamTaskGroupSolutions] = useState<ExamSheetImageZoneTableItem[]>([]);
  const [taskImageZonesDeletingConfirmData, setTaskImageZonesDeletingConfirmData] = useState<
    ExamTaskImageZone | undefined
  >(undefined);
  const [schemaImageZonesDeletingConfirmData, setSchemaImageZonesDeletingConfirmData] = useState<
    { task: ExamTaskImageZone; schema: ExamScoringSchemaImageZone } | undefined
  >(undefined);

  const [examTaskZoneForEditing, setExamTaskZoneForEditing] = useState<ExamTaskImageZone | undefined>(undefined);
  const [examScoringSchemaZoneForEditing, setExamScoringSchemaZoneForEditing] = useState<
    ExamScoringSchemaImageZone | undefined
  >(undefined);

  const openImageZonesEditor = (
    examTaskImageZone: ExamTaskImageZone | undefined,
    examScoringSchemaImageZone: ExamScoringSchemaImageZone | undefined,
  ) => {
    setExamTaskZoneForEditing(examTaskImageZone);
    setExamScoringSchemaZoneForEditing(examScoringSchemaImageZone);
  };

  const getActionsMenuIconForImageZones = (
    examTaskImageZone: ExamTaskImageZone,
    examScoringSchemaImageZone: ExamScoringSchemaImageZone | null = null,
  ): JSX.Element => {
    return (
      <ActionsMenu
        menuItems={[
          {
            text: 'Edytuj strefę obrazu',
            onClick: () =>
              openImageZonesEditor(
                examTaskImageZone ? examTaskImageZone : undefined,
                examScoringSchemaImageZone ? examScoringSchemaImageZone : undefined,
              ),
          },
          {
            text: 'Usuń strefę obrazu',
            onClick: () => {
              if (examScoringSchemaImageZone && examTaskImageZone) {
                setSchemaImageZonesDeletingConfirmData({
                  task: examTaskImageZone,
                  schema: examScoringSchemaImageZone,
                });
              } else if (examTaskImageZone) {
                setTaskImageZonesDeletingConfirmData(examTaskImageZone);
              }
            },
          },
        ]}
      />
    );
  };

  const getActionsMenuIconForEmptyImageZones = (
    examTaskZone: ExamTaskImageZone | undefined,
    examScoringSchemaZone: ExamScoringSchemaImageZone | undefined,
  ): JSX.Element => {
    return (
      <TooltipOne title="Dodaj strefę obrazu" placement="top">
        <IconButton disableRipple size="small" onClick={() => openImageZonesEditor(examTaskZone, examScoringSchemaZone)}>
          <AddIcon className={zoneClasses.addImageZoneIcon} />
        </IconButton>
      </TooltipOne>
    );
  };

  const refreshTable = () => {
    getExamSheetImageZones(props.sheetId).then(
      (response: AxiosResponse<ExamTaskImageZone[]>) => {
        setExamSheetImageZones(response.data);

        const tableItems: ExamSheetImageZoneTableItem[] = [];
        response.data.forEach((examTaskImageZoneData: ExamTaskImageZone) => {
          tableItems.push({
            id: examTaskImageZoneData.examTaskId,
            examTaskName: (
              <div className={zoneClasses.textCell}>{`Zadanie ${examTaskImageZoneData.examTaskNumber}`}</div>
            ),
            criterionName: null,
            imageZone: examTaskImageZoneData.taskImageZoneIds.length ? (
              <div className={zoneClasses.centeredCell}>
                <FilterIcon
                  className={zoneClasses.zoneExistsIcon}
                  aria-label={`Ikona wskazująca na obecność strefy obrazu dla zadania ${examTaskImageZoneData.examTaskNumber}`}
                />
              </div>
            ) : null,
            actions: examTaskImageZoneData.taskImageZoneIds.length
              ? getActionsMenuIconForImageZones(examTaskImageZoneData)
              : getActionsMenuIconForEmptyImageZones(examTaskImageZoneData, undefined),
          });

          examTaskImageZoneData.examScoringSchemas.forEach((schemaImageZoneData: ExamScoringSchemaImageZone) => {
            tableItems.push({
              id: `${examTaskImageZoneData.examTaskId}_${schemaImageZoneData.examScoringSchemaId}`,
              examTaskName: null,
              criterionName: (
                <div className={zoneClasses.textCell} style={{ width: '350px' }}>
                  {schemaImageZoneData.criterionNumber}-{schemaImageZoneData.criterionName} ({schemaImageZoneData.label}
                  )
                </div>
              ),
              imageZone: schemaImageZoneData.taskImageZoneId.length ? (
                <div className={zoneClasses.centeredCell}>
                  <FilterIcon
                    className={zoneClasses.zoneExistsIcon}
                    aria-label={`Ikona wskazująca na obecność strefy obrazu dla kryterium ${schemaImageZoneData.criterionNumber}-${schemaImageZoneData.criterionName} (${schemaImageZoneData.label}) zadania ${examTaskImageZoneData.examTaskNumber}`}
                  />
                </div>
              ) : null,
              actions: schemaImageZoneData.taskImageZoneId.length
                ? getActionsMenuIconForImageZones(examTaskImageZoneData, schemaImageZoneData)
                : getActionsMenuIconForEmptyImageZones(examTaskImageZoneData, schemaImageZoneData),
            });
          });
        });
        setExamTaskGroupSolutions(tableItems);
      },
      () => {
        console.log('error');
      },
    );
  };

  useEffect(() => {
    refreshTable();
  }, []);

  const handleDeleteSuccess = () => {
    setTaskImageZonesDeletingConfirmData(undefined);
    setSchemaImageZonesDeletingConfirmData(undefined);
    openAlert('success', 'Wybrana strefa obrazu została pomyślnie usunięta');
    refreshTable();
  };

  const handleDeleteError = () => {
    setTaskImageZonesDeletingConfirmData(undefined);
    setSchemaImageZonesDeletingConfirmData(undefined);
    openAlert('error', 'Usunięcie wybranej strefy obrazu nie powiodło się. Spróbuj ponownie później');
  };

  const handleDeleteTaskImageZones = () =>
    taskImageZonesDeletingConfirmData &&
    deleteExamTaskImageZones(taskImageZonesDeletingConfirmData.examTaskId).then(handleDeleteSuccess, handleDeleteError);

  const handleDeleteSchemaImageZones = () =>
    schemaImageZonesDeletingConfirmData &&
    deleteExamScoringSchemaImageZones(schemaImageZonesDeletingConfirmData.schema.examScoringSchemaId).then(
      handleDeleteSuccess,
      handleDeleteError,
    );

  const closeImageZonesEditor = () => {
    setExamTaskZoneForEditing(undefined);
    setExamScoringSchemaZoneForEditing(undefined);
    refreshTable();
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.title}>Strefa obrazu</Typography>
        </Grid>
        <Grid item xs={12}>
          {examSheetImageZones && !examSheetImageZones.length ? (
            <InfoShield
              icon={<FilterIcon style={{ fontSize: '80px', color: '#BCBEDC' }} />}
              message="W tej chwili akrusz nie jest skonfigurowany i nie można definiować stref obrazu."
            />
          ) : (
            <div className={zoneClasses.table}>
              <SelectableTable<ExamSheetImageZoneTableItem>
                columns={columns}
                rows={examTaskGroupSolutions}
                isMultiChoice={true}
                caption={"Strefa obrazu"}
                noOverflowInitial={false}
              />
            </div>
          )}
        </Grid>
      </Grid>
      {taskImageZonesDeletingConfirmData && (
        <Prompt
          type="confirmation"
          open={true}
          size="sm"
          onCancel={() => setTaskImageZonesDeletingConfirmData(undefined)}
          onConfirm={handleDeleteTaskImageZones}
          mainActionButtonText="TAK"
          mainText={
            <>
              Czy na pewno chcesz usunąć strefę/y obrazu dla:
              <br /> Zadanie {taskImageZonesDeletingConfirmData.examTaskNumber}?
            </>
          }
        />
      )}
      {schemaImageZonesDeletingConfirmData && (
        <Prompt
          type="confirmation"
          open={true}
          size="sm"
          onCancel={() => setSchemaImageZonesDeletingConfirmData(undefined)}
          onConfirm={handleDeleteSchemaImageZones}
          mainActionButtonText="TAK"
          mainText={
            <>
              Czy na pewno chcesz usunąć strefę/y obrazu dla:
              <br /> Zadanie {schemaImageZonesDeletingConfirmData.task.examTaskNumber}, Kryterium{' '}
              {schemaImageZonesDeletingConfirmData.schema.criterionNumber}-
              {schemaImageZonesDeletingConfirmData.schema.criterionName}(
              {schemaImageZonesDeletingConfirmData.schema.label})
            </>
          }
        />
      )}
      {examTaskZoneForEditing && (
        <ImageZonesEditor
          examSheetId={props.sheetId}
          sheetSymbol={props.sheetSymbol}
          examTaskZone={examTaskZoneForEditing}
          examScoringSchemaZone={examScoringSchemaZoneForEditing}
          onCancel={closeImageZonesEditor}
          onClose={closeImageZonesEditor}
        />
      )}
    </>
  );
};

export default ImageZonesList;
